import PropTypes from "prop-types";
import React, { useEffect, useRef } from "react";

const MultiSelectDropdown = ({
  label,
  value = [],
  options = [],
  onChange,
  error = null,
  required = false,
  className = "",
  name,
  openDropdown,
  setOpenDropdown,
}) => {
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setOpenDropdown]);

  const handleOptionClick = (option, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const currentValues = Array.isArray(value) ? [...value] : [];
    const isAlreadySelected = currentValues.includes(option);

    const newValues = isAlreadySelected
      ? currentValues.filter((item) => item !== option)
      : [...currentValues, option];

    onChange(newValues);
  };

  const isSelected = (option) => {
    return Array.isArray(value) && value.includes(option);
  };

  const formatDisplayValue = (values) => {
    if (!Array.isArray(values) || values.length === 0) {
      return "Select options";
    }

    // Sort values based on their order in options array
    const sortedValues = [...values].sort((a, b) => {
      return options.indexOf(a) - options.indexOf(b);
    });

    return sortedValues.join(", ");
  };

  const isOpen = openDropdown === name;
  const displayValue = formatDisplayValue(value);

  return (
    <div className={`multi-dropdown-field ${className}`} data-open={isOpen}>
      <label>
        {label} {required && <span className="required">*</span>}
      </label>
      <div
        className={`select-wrapper ${error ? "error" : ""}`}
        ref={dropdownRef}
      >
        <div
          className={`select-display ${error ? "error" : ""}`}
          onClick={() => setOpenDropdown(isOpen ? null : name)}
          tabIndex={0}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              setOpenDropdown(null);
            }
          }}
        >
          {displayValue}
        </div>
        {error && <div className="error-message">{error}</div>}
        {isOpen && (
          <div className="dropdown-menu">
            {options.map((option) => (
              <div
                key={option}
                className={`option ${isSelected(option) ? "selected" : ""}`}
                onClick={(e) => handleOptionClick(option, e)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleOptionClick(option, e);
                }}
              >
                <div className="checkbox">
                  {isSelected(option) && <div className="checkbox-inner" />}
                </div>
                {option}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

MultiSelectDropdown.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  required: PropTypes.bool,
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  openDropdown: PropTypes.string,
  setOpenDropdown: PropTypes.func.isRequired,
};

export default MultiSelectDropdown;
